import type { StaffRole } from "@lafayette/auth";
import type { StaffCatalogItemStatus } from "@lafayette/schemas";

import { type CapabilityRules, capabilityFlags } from "../access/capabilities";

// What a staff member may do in the catalog UI. This is presentation only: the API decides every
// request again. The roles come from the domain's `capabilityRoles`. The transition table repeats
// `catalogTransitions` (C1 to C9) and is pinned by a test, so a drift shows up as a failing test and
// never as a button that the API then refuses.

export type CatalogCapabilities = Readonly<{
  /** `catalog.read_internal`: the list, the detail, the history. */
  read: boolean;
  /** `catalog.edit`: create, edit, upload, submit for review, return to draft. */
  edit: boolean;
  /** `catalog.publish`: publish, unpublish, archive. */
  publish: boolean;
  /** `catalog.authorize_price`: mark a fixed price payment-grade. */
  authorizePrice: boolean;
  /** `catalog.read_internal`: the internal notes panel. */
  readNotes: boolean;
  /** `catalog.add_internal_note`. */
  addNote: boolean;
}>;

const catalogCapabilityRules = {
  read: "catalog.read_internal",
  edit: "catalog.edit",
  publish: "catalog.publish",
  authorizePrice: "catalog.authorize_price",
  readNotes: "catalog.read_internal",
  addNote: "catalog.add_internal_note",
} as const satisfies CapabilityRules;

export function capabilitiesForRoles(roles: readonly StaffRole[]): CatalogCapabilities {
  return capabilityFlags(catalogCapabilityRules, roles);
}

export type TransitionAction =
  | "submitForReview"
  | "returnToDraft"
  | "publish"
  | "unpublish"
  | "archive";

export type AllowedAction = Readonly<{
  action: TransitionAction;
  label: string;
  /** Unpublish and archive need a reason. */
  needsReason: boolean;
  /** Publish is disabled while a BLOCKING issue stands. */
  needsPublishable: boolean;
  tone: "primary" | "neutral" | "danger";
}>;

type Rule = Readonly<{
  from: StaffCatalogItemStatus;
  action: TransitionAction;
  capability: "edit" | "publish";
  label: string;
  needsReason: boolean;
  tone: AllowedAction["tone"];
}>;

const rules: readonly Rule[] = [
  {
    from: "DRAFT",
    action: "submitForReview",
    capability: "edit",
    label: "Submit for review",
    needsReason: false,
    tone: "primary",
  },
  {
    from: "REVIEW",
    action: "publish",
    capability: "publish",
    label: "Publish",
    needsReason: false,
    tone: "primary",
  },
  {
    from: "REVIEW",
    action: "returnToDraft",
    capability: "edit",
    label: "Return to draft",
    needsReason: false,
    tone: "neutral",
  },
  {
    from: "PUBLISHED",
    action: "unpublish",
    capability: "publish",
    label: "Unpublish",
    needsReason: true,
    tone: "neutral",
  },
  {
    from: "UNPUBLISHED",
    action: "publish",
    capability: "publish",
    label: "Publish again",
    needsReason: false,
    tone: "primary",
  },
  {
    from: "UNPUBLISHED",
    action: "returnToDraft",
    capability: "edit",
    label: "Return to draft",
    needsReason: false,
    tone: "neutral",
  },
  {
    from: "DRAFT",
    action: "archive",
    capability: "publish",
    label: "Archive",
    needsReason: true,
    tone: "danger",
  },
  {
    from: "REVIEW",
    action: "archive",
    capability: "publish",
    label: "Archive",
    needsReason: true,
    tone: "danger",
  },
  {
    from: "UNPUBLISHED",
    action: "archive",
    capability: "publish",
    label: "Archive",
    needsReason: true,
    tone: "danger",
  },
];

/** The transitions the status allows and the capabilities permit, in display order. */
export function allowedActions(
  status: StaffCatalogItemStatus,
  capabilities: CatalogCapabilities,
): readonly AllowedAction[] {
  return rules
    .filter((rule) => rule.from === status && capabilities[rule.capability])
    .map((rule) => ({
      action: rule.action,
      label: rule.label,
      needsReason: rule.needsReason,
      needsPublishable: rule.action === "publish",
      tone: rule.tone,
    }));
}

export type EditAccess =
  | Readonly<{ readOnly: false; reason: null }>
  | Readonly<{ readOnly: true; reason: "no_capability" | "archived" }>;

/**
 * Whether the fields are editable. A PUBLISHED item stays editable in place (owner decision Q2: the API
 * re-checks publishability and the UI shows a refusal). An ARCHIVED item never is.
 */
export function editAccess(
  status: StaffCatalogItemStatus,
  capabilities: CatalogCapabilities,
): EditAccess {
  if (!capabilities.edit) return { readOnly: true, reason: "no_capability" };
  if (status === "ARCHIVED") return { readOnly: true, reason: "archived" };
  return { readOnly: false, reason: null };
}
